import { A, G } from '@mobily/ts-belt';
import { Box, Card, Chip, Typography } from '@mui/material';
import { JSX, useMemo } from 'react';
import {
  BooleanField,
  FunctionField,
  NumberField,
  ReferenceField,
  ReferenceManyField,
  SingleFieldList,
  TextField,
  useGetList,
  useGetOne,
  useTranslate,
  WithRecord,
} from 'react-admin';

import { allowsUnilateralMatchingContribution } from '@Common/types/b2b/plans';
import { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import { B2bCompanyPlan, B2bCompanySharesPlan, getPlanTypeName } from '@Common/types/bo/b2b/plans';
import { GetInstrumentOneResponse, GetInstrumentsResponseItem } from '@Common/types/bo/instruments';
import { B2bCompanyPlanExternal } from '@Common/types/bo_external/plans';
import { BOResourceAll } from '@Common/types/bo_external/resources';

import {
  cashSourcesChoices,
  matchingContributionComputedByChoices,
  matchingContributionPoolType,
  matchingContributionRuleType,
} from '@sh4res/bo-common/choices/matching_contribution';
import { DatagridTyped, ShowFields, ShowSection } from '@sh4res/bo-common/components';
import { B2bPlansProps } from '@sh4res/bo-common/components/B2bPlans/B2bPlansTypes';
import { B2bPlanEntryFeesTable } from '@sh4res/bo-common/components/B2bPlans/components/B2bPlanEntryFeesTable';
import { CcbSection } from '@sh4res/bo-common/components/B2bPlans/components/CcbSection';
import { MatchingContributionSettings } from '@sh4res/bo-common/components/B2bPlans/components/MatchingContributionSettings';
import { useB2bPlanEntryFees } from '@sh4res/bo-common/components/B2bPlans/components/useB2bPlanEntryFees';
import {
  CurrencyField,
  IdField,
  IdListField,
  SelectChipField,
  SelectChipFieldsList,
} from '@sh4res/bo-common/fields';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

export const B2bPlan = ({
  plan,
  editButton,
}: {
  plan: B2bCompanyPlan | B2bCompanyPlanExternal;
  resource: BOResourceAll;
  editButton: B2bPlansProps['editButton'];
}) => {
  const translate = useTranslate();
  const isBoExt = isBoExternal();
  const basePath = useResourceBasePath();
  const allInstruments = useMemo(
    () => plan.shares_plans.flatMap((sharesPlan) => sharesPlan.instruments_universe),
    [plan.shares_plans],
  );
  const { data: instrument } = useGetOne<GetInstrumentOneResponse>(
    'instruments',
    {
      id: G.isNotNullable(plan.default_fund_instrument_id) ? plan.default_fund_instrument_id : 0,
    },
    {
      enabled: G.isNotNullable(plan.default_fund_instrument_id) && isBoExt,
    },
  );

  const { data: instrumentsData, isFetched } = useGetList<GetInstrumentsResponseItem>(
    'instruments',
    {
      filter: {
        id: allInstruments,
      },
      sort: { field: 'legal_status', order: 'DESC' },
      pagination: { page: 1, perPage: allInstruments.length },
    },
    {
      enabled: !isBoExt && allInstruments.length > 0,
    },
  );

  const planEntryFeesBase = 'fees' in plan && plan.fees.entry_fee;
  const entryFees = useB2bPlanEntryFees({ instrumentsData, isFetched, planEntryFeesBase });

  const instrumentCashSourcesBase = plan.instruments_cash_sources_libre;
  const instrumentCashSources = useMemo(() => {
    if (!isFetched) {
      return [];
    }

    return (instrumentsData || []).flatMap((instrument) => {
      const instrumentCashSources = instrumentCashSourcesBase.find(
        (source) => source.instrument_id === instrument.id,
      );
      if (!instrumentCashSources) {
        return [];
      }
      return [
        {
          ...instrumentCashSources,
          id: instrument.id,
          instrument: instrument.short_name,
          isin: instrument.isin,
        },
      ];
    });
  }, [instrumentsData, isFetched, instrumentCashSourcesBase]);

  const ccbInstrumentsUniverse = useMemo(
    () => instrumentsData?.filter((instrument) => instrument.type === 'ccb') ?? [],
    [instrumentsData],
  );

  const savingPlanTypeName = getPlanTypeName(
    plan.shares_plans.length === 1 ? plan.shares_plans[0].type : plan.category,
    plan.contract_type,
  );

  return (
    <Box sx={{ marginTop: '5px', paddingBottom: '5px' }} key={plan.id} id={`product-${plan.id}`}>
      <ShowSection
        label={savingPlanTypeName}
        labelParams={{ _: savingPlanTypeName }}
        record={plan}
        sx={{ paddingBottom: '15px', position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: 5, right: 5 }}>{editButton}</Box>
        <Card
          sx={{
            marginLeft: '15px',
            marginRight: '15px',
            '&>*>:nth-child(-n+2)': {
              borderTop: 'none',
            },
          }}>
          <ShowFields<B2bCompanyPlan>
            resource="bo_common.b2b.companies.saving_plans"
            fields={{
              id: <NumberField data-testid="BoExternalB2bPlan_IdField" />,
              category: <TextField data-testid="BoExternalB2bPlan_CategoryField" />,
              minimum_tenure: <NumberField data-testid="BoExternalB2bPlan_MinimumTenureField" />,
              minimum_tenure_reference_date: (
                <TextField data-testid="BoExternalB2bPlan_MinimumTenureReferenceDateField" />
              ),
              cash_sources: <SelectChipFieldsList choices={cashSourcesChoices} />,
              social_contribution: (
                <FunctionField
                  data-testid="BoExternalB2bPlan_SocialContributionField"
                  render={(record: B2bCompanyPlan) => `${record.social_contribution}% of PASS`}
                />
              ),
              matching_contribution_product_limit: (
                <CurrencyField
                  currency="EUR"
                  testId="BoExternalB2bPlan_MatchingContributionProductLimitField"
                />
              ),
              ...(allowsUnilateralMatchingContribution(plan.category) && {
                unilateral_matching_contribution_limit: (
                  <WithRecord<B2bCompanyPlan>
                    render={(record) =>
                      G.isNullable(record.unilateral_matching_contribution_limit) ? (
                        <Typography
                          variant="body2"
                          data-testid="BoExternalB2bPlan_UnilateralMatchingContributionLimitField">
                          {translate('resources.bo_common.no')}
                        </Typography>
                      ) : (
                        <CurrencyField
                          source="unilateral_matching_contribution_limit"
                          currency="EUR"
                          testId="BoExternalB2bPlan_UnilateralMatchingContributionLimitField"
                        />
                      )
                    }
                  />
                ),
              }),
              ...(G.isNotNullable(plan.default_fund_instrument_id) && {
                default_fund_instrument_id: (
                  <ReferenceField
                    source="default_fund_instrument_id"
                    reference="instruments"
                    link="show"
                    data-testid="BoExternalB2bPlan_DefaultFundInstrumentIdField">
                    {isBoExt && <span>{instrument?.name}</span>}
                    {!isBoExt && (
                      <TextField
                        source="name"
                        data-testid="BoExternalB2bPlan_DefaultFundInstrumentNameField"
                      />
                    )}
                  </ReferenceField>
                ),
              }),
              ...(G.isNotNullable(plan.default_investor_profile) && {
                default_investor_profile: (
                  <WithRecord<B2bCompanyPlan>
                    render={(record) =>
                      G.isNotNullable(record.default_investor_profile) && (
                        <IdField
                          record={record.default_investor_profile}
                          source="id"
                          textSource="name"
                          reference={`${basePath}investor_profile`}
                          data-testid="BoExternalB2bPlan_DefaultInvestorProfileField"
                        />
                      )
                    }
                  />
                ),
              }),
              ...(G.isNotNullable(plan.other_investor_profiles) &&
                A.isNotEmpty(plan.other_investor_profiles) && {
                  other_investor_profiles: (
                    <IdListField
                      source="other_investor_profiles"
                      textSource="name"
                      reference={`${basePath}investor_profile`}
                      showAllLink={false}
                      data-testid="BoExternalB2bPlan_OtherInvestorProfilesField"
                    />
                  ),
                }),
            }}
          />

          {!isBoExt && (
            <ShowSection label="" noCard={true}>
              <DatagridTyped<
                Omit<B2bCompanySharesPlan, 'management_fee_charge_frequency' | 'instruments_locked'>
              >
                columnRegistry={{
                  // eslint-disable-next-line @sh4res/jsx-id-field-for-resources -- no show page
                  id: <NumberField data-testid="BoExternalB2bPlan_SharesPlanIdField" />,
                  name: <TextField data-testid="BoExternalB2bPlan_SharesPlanNameField" />,
                  plan_label: <TextField data-testid="BoExternalB2bPlan_SharesPlanLabelField" />,
                  type: (
                    <FunctionField
                      label="resources.bo_common.b2b.companies.saving_plans.fields.type"
                      data-testid="BoExternalB2bPlan_SharesPlanTypeField"
                      render={(record: B2bCompanySharesPlan) =>
                        getPlanTypeName(record.type, plan.contract_type)
                      }
                    />
                  ),
                  rebalancing_frequency: <Typography>&nbsp;</Typography>,
                  retirement_age: <Typography>&nbsp;</Typography>,
                  instruments_universe: (
                    <ReferenceManyField
                      label="resources.bo_common.b2b.companies.saving_plans.fields.instruments_universe"
                      source="instruments_universe"
                      target="id"
                      reference="instruments"
                      filter={{ type: ['mutual-fund'] }}
                      sort={{ field: isBoExternal() ? 'short_name' : 'name', order: 'ASC' }}
                      perPage={1000}
                      data-testid="BoExternalB2bPlan_InstrumentsUniverseField">
                      <SingleFieldList
                        linkType="show"
                        sx={{
                          gap: '0px',
                          ['& .RaSingleFieldList-link']: {
                            margin: '2px',
                          },
                        }}>
                        <FunctionField
                          label="Name"
                          data-testid="BoExternalB2bPlan_InstrumentNameField"
                          render={(instrument: GetInstrumentsResponseItem) => (
                            <Chip
                              label={
                                (isBoExternal() ? instrument.short_name : instrument.name) ||
                                instrument.symbol
                              }
                            />
                          )}
                        />
                      </SingleFieldList>
                    </ReferenceManyField>
                  ),
                }}
                showCard={true}
                displayedColumns={['type', 'instruments_universe']}
                data={plan.shares_plans}
                sort={{ field: 'name', order: 'ASC' }}
                sortableColumns={[]}
              />
            </ShowSection>
          )}

          <ShowSection label="" noCard={true}>
            <DatagridTyped<
              Omit<B2bCompanySharesPlan, 'management_fee_charge_frequency' | 'instruments_locked'>
            >
              bulkActionButtons={false}
              columnRegistry={{
                // eslint-disable-next-line @sh4res/jsx-id-field-for-resources -- no show page
                id: <NumberField data-testid="BoExternalB2bPlan_PlanIdField" />,
                name: <TextField data-testid="BoExternalB2bPlan_PlanNameField" />,
                plan_label: <TextField data-testid="BoExternalB2bPlan_PlanPlanLabelField" />,
                type: (
                  <FunctionField
                    data-testid="BoExternalB2bPlan_PlanTypeField"
                    render={(record: B2bCompanySharesPlan) =>
                      getPlanTypeName(record.type, plan.contract_type)
                    }
                  />
                ),
                rebalancing_frequency: (
                  <FunctionField
                    label="resources.bo_common.b2b.companies.saving_plans.fields.rebalancing_frequency"
                    data-testid="BoExternalB2bPlan_PlanRebalancingFrequencyField"
                    render={(record: B2bCompanySharesPlan) =>
                      record.rebalancing_frequency
                        ? translate(
                            `resources.bo_common.b2b.companies.saving_plans.rebalancing_frequency.values.${record.rebalancing_frequency}`,
                          )
                        : ''
                    }
                  />
                ),
                retirement_age: (
                  <NumberField
                    label="resources.bo_common.b2b.companies.saving_plans.fields.retirement_age"
                    data-testid="BoExternalB2bPlan_PlanRetirementAgeField"
                    emptyText="-"
                  />
                ),
                instruments_universe: <Typography>&nbsp;</Typography>,
              }}
              showCard={true}
              displayedColumns={[
                'type',
                'plan_label',
                ...(plan.shares_plans.some((plan) => plan.rebalancing_frequency !== null)
                  ? [
                      'rebalancing_frequency' as keyof Omit<
                        B2bCompanySharesPlan,
                        'management_fee_charge_frequency' | 'instruments_locked'
                      >,
                    ]
                  : []),
                ...(!isBoExt &&
                plan.shares_plans.some((plan) => G.isNotNullable(plan.retirement_age))
                  ? [
                      'retirement_age' as keyof Omit<
                        B2bCompanySharesPlan,
                        'management_fee_charge_frequency' | 'instruments_locked'
                      >,
                    ]
                  : []),
              ]}
              data={plan.shares_plans}
              sort={{ field: 'name', order: 'ASC' }}
              sortableColumns={[]}
            />
          </ShowSection>

          {G.isNotNullable(plan.ccb_config) && (
            <>
              <br />
              <CcbSection ccbConfig={{ ...plan.ccb_config, instruments: ccbInstrumentsUniverse }} />
            </>
          )}

          <br />

          <ShowSection
            label="resources.bo_common.b2b.companies.saving_plans.matching_contribution_table_title"
            noCard={true}>
            <ShowFields<B2bCompanyPlan & Record<'mc_enabled' | 'configuration_type', unknown>>
              resource="bo_common.b2b.companies.saving_plans"
              fields={{
                mc_enabled: (
                  <>
                    <BooleanField
                      source="mc_enabled"
                      record={{
                        mc_enabled:
                          plan.matching_contribution_config !== null ||
                          plan.has_custom_matching_contribution_config === true,
                      }}
                      data-testid="BoExternalB2bPlan_McEnabledField"
                    />
                  </>
                ),
                matching_contribution_computed_by: (
                  <>
                    <SelectChipField
                      source="matching_contribution_computed_by"
                      choices={matchingContributionComputedByChoices}
                      emptyText="-"
                      data-testid="BoExternalB2bPlan_MatchingContributionComputedByField"
                    />
                  </>
                ),
                configuration_type: (
                  <>
                    {plan.has_custom_matching_contribution_config ? (
                      <Typography variant="body1">
                        {translate(
                          'resources.bo_common.b2b.companies.saving_plans.has_custom_matching_contribution_config',
                        )}
                      </Typography>
                    ) : (
                      <SelectChipField
                        choices={matchingContributionRuleType}
                        source="matching_contribution_config.type"
                        data-testid="BoExternalB2bPlan_ConfigurationTypeField"
                      />
                    )}
                  </>
                ),
                matching_contribution_pool_type: (
                  <>
                    <SelectChipField
                      choices={matchingContributionPoolType}
                      source="matching_contribution_pool_type"
                      data-testid="BoExternalB2bPlan_MatchingContributionPoolTypeField"
                    />
                  </>
                ),
              }}
              borderBottom={false}
            />
            <MatchingContributionSettings plan={plan} />
          </ShowSection>

          {'fees' in plan && (
            <>
              <ShowSection
                label="resources.bo_common.b2b.companies.saving_plans.entry_fees_table.title"
                noCard={true}>
                <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                  {translate(
                    'resources.bo_common.b2b.companies.saving_plans.entry_fees_table.employee_pct_note',
                  )}
                </Typography>
                <B2bPlanEntryFeesTable entryFees={entryFees} />
              </ShowSection>
              {plan.is_cash_sources_per_instrument && (
                <ShowSection
                  label="resources.bo_common.b2b.companies.saving_plans.custom_cash_sources.title"
                  noCard={true}>
                  <DatagridTyped
                    columnRegistry={{
                      instrument_id: (
                        <IdField
                          reference="instruments"
                          data-testid="BoExternalB2bPlan_CashSourceInstrumentIdField"
                        />
                      ),
                      instrument: (
                        <TextField
                          label="resources.bo_common.b2b.companies.saving_plans.custom_cash_sources.instrument"
                          data-testid="BoExternalB2bPlan_CashSourceInstrumentField"
                        />
                      ),
                      isin: (
                        <TextField
                          label="resources.bo_common.b2b.companies.saving_plans.custom_cash_sources.isin"
                          data-testid="BoExternalB2bPlan_CashSourceIsinField"
                        />
                      ),
                      ...plan.cash_sources.reduce<Record<B2bCashSource, JSX.Element>>(
                        // oxlint-disable-next-line react/no-unstable-nested-components -- render callback, not a component
                        (acc, source) => {
                          acc[source] = (
                            <FunctionField
                              data-testid={`BoExternalB2bPlan_CashSource_${source}_ColField`}
                              render={(record) => (
                                <BooleanField
                                  source="source"
                                  record={{
                                    source: plan.instruments_cash_sources_libre
                                      .find((entry) => entry.instrument_id === record.instrument_id)
                                      ?.cash_sources?.includes(source),
                                  }}
                                  data-testid={`BoExternalB2bPlan_CashSource_${source}_BoolField`}
                                />
                              )}
                              label={`resources.bo_common.b2b.companies.saving_plans.cash_sources.${source}`}
                            />
                          );
                          return acc;
                        },
                        {} as Record<B2bCashSource, JSX.Element>,
                      ),
                      mc_enabled: (
                        <FunctionField
                          data-testid="BoExternalB2bPlan_McEnabledFuncField"
                          render={(record) => (
                            <BooleanField
                              source="source"
                              record={{
                                source: !!plan.instruments_cash_sources_libre.find(
                                  (entry) => entry.instrument_id === record.instrument_id,
                                )?.is_matching_contributions_enabled,
                              }}
                              data-testid="BoExternalB2bPlan_McEnabledBoolField"
                            />
                          )}
                          label="resources.bo_common.b2b.companies.saving_plans.custom_cash_sources.matching_contribution"
                        />
                      ),
                    }}
                    showCard={true}
                    data={instrumentCashSources}
                    sortableColumns={[]}
                    displayedColumns={['instrument', 'isin', ...plan.cash_sources, 'mc_enabled']}
                    sort={{ field: 'instrument_id', order: 'ASC' }}
                  />
                </ShowSection>
              )}
            </>
          )}
        </Card>
      </ShowSection>
    </Box>
  );
};
